import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const SocialProof = () => {
  const [contributionData, setContributionData] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Mock GitHub contribution data
  useEffect(() => {
    const generateContributions = () => {
      const data = [];
      const today = new Date();
      
      for (let i = 364; i >= 0; i--) {
        const date = new Date(today);
        date?.setDate(date?.getDate() - i);
        
        // Generate realistic contribution pattern
        const dayOfWeek = date?.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const baseContributions = isWeekend ? 0 : Math.floor(Math.random() * 8);
        
        data?.push({
          date: date?.toISOString()?.split('T')?.[0],
          count: baseContributions,
          level: baseContributions === 0 ? 0 : Math.min(Math.floor(baseContributions / 2) + 1, 4)
        });
      }
      
      return data;
    };

    setContributionData(generateContributions());
  }, []);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      role: "Senior Product Manager",
      company: "TechFlow Inc.",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      content: `Working with this developer was exceptional. They delivered a complex React dashboard ahead of schedule and the code quality was outstanding. Their attention to performance optimization saved us significant infrastructure costs.`,
      rating: 5,
      project: "Analytics Dashboard"
    },
    {
      id: 2,
      name: "Marcus Rodriguez",
      role: "CTO",
      company: "StartupLab",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
      content: `Incredible problem-solving skills and technical depth. They architected our entire backend infrastructure and it's been running flawlessly for over a year. Communication was clear and professional throughout.`,
      rating: 5,
      project: "Backend Architecture"
    },
    {
      id: 3,
      name: "Emily Watson",
      role: "Lead Developer",
      company: "Digital Solutions Co.",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      content: `A true full-stack expert who understands both frontend and backend intricacies. Their code reviews improved our entire team's skills. Would definitely collaborate again on future projects.`,
      rating: 5,
      project: "Full-Stack Application"
    }
  ];

  const achievements = [
    {
      icon: "Star",
      value: "2.5K+",
      label: "GitHub Stars",
      description: "Across open source projects"
    },
    {
      icon: "GitFork",
      value: "450+",
      label: "Repository Forks",
      description: "Community contributions"
    },
    {
      icon: "Users",
      value: "15+",
      label: "Happy Clients",
      description: "Successful collaborations"
    },
    {
      icon: "Award",
      value: "98%",
      label: "Project Success Rate",
      description: "On-time delivery"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  const getContributionColor = (level) => {
    const colors = [
      'bg-border',
      'bg-success/30',
      'bg-success/60',
      'bg-success/80',
      'bg-success'
    ];
    return colors?.[level] || colors?.[0];
  };

  const totalContributions = contributionData?.reduce((sum, day) => sum + day?.count, 0);
  const currentStreak = contributionData?.slice(-30)?.filter(day => day?.count > 0)?.length;

  return (
    <section className="py-20 bg-muted">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Trophy" size={20} className="text-accent" />
            <span className="text-sm font-medium text-text-secondary tracking-wide uppercase">
              Social Proof
            </span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
            Trusted by Developers & Teams
          </h2>
          
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Recognition from the community, successful collaborations, 
            and consistent contributions to the developer ecosystem.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* GitHub Contributions */}
          <div className="animate-fade-in-up">
            <div className="bg-card rounded-xl p-6 shadow-soft">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Icon name="Github" size={24} className="text-primary" />
                  <div>
                    <h3 className="text-xl font-bold text-primary">GitHub Activity</h3>
                    <p className="text-sm text-text-secondary">Last 12 months</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-2xl font-bold text-success">{totalContributions}</div>
                  <div className="text-xs text-text-secondary">contributions</div>
                </div>
              </div>

              {/* Contribution Graph */}
              <div className="mb-6">
                <div className="grid grid-cols-53 gap-1 mb-4">
                  {contributionData?.map((day, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-sm ${getContributionColor(day?.level)} transition-colors duration-200`}
                      title={`${day?.count} contributions on ${day?.date}`}
                    />
                  ))}
                </div>
                
                <div className="flex items-center justify-between text-xs text-text-secondary">
                  <span>Less</span>
                  <div className="flex space-x-1">
                    {[0, 1, 2, 3, 4]?.map((level) => (
                      <div
                        key={level}
                        className={`w-2 h-2 rounded-sm ${getContributionColor(level)}`}
                      />
                    ))}
                  </div>
                  <span>More</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-lg font-bold text-primary">{currentStreak}</div>
                  <div className="text-xs text-text-secondary">Day Streak</div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-lg font-bold text-primary">25</div>
                  <div className="text-xs text-text-secondary">Repositories</div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <div className="bg-card rounded-xl p-6 shadow-soft">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="MessageSquare" size={24} className="text-accent" />
                <div>
                  <h3 className="text-xl font-bold text-primary">Client Testimonials</h3>
                  <p className="text-sm text-text-secondary">What people say</p>
                </div>
              </div>

              {/* Current Testimonial */}
              <div className="relative min-h-[200px]">
                {testimonials?.map((testimonial, index) => (
                  <div
                    key={testimonial?.id}
                    className={`absolute inset-0 transition-all duration-500 ${
                      index === currentTestimonial 
                        ? 'opacity-100 transform translate-x-0' 
                        : 'opacity-0 transform translate-x-4'
                    }`}
                  >
                    {/* Rating */}
                    <div className="flex space-x-1 mb-4">
                      {[...Array(testimonial?.rating)]?.map((_, i) => (
                        <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
                      ))}
                    </div>

                    {/* Content */}
                    <blockquote className="text-text-secondary mb-6 leading-relaxed">
                      "{testimonial?.content}"
                    </blockquote>

                    {/* Author */}
                    <div className="flex items-center space-x-4">
                      <Image
                        src={testimonial?.avatar}
                        alt={testimonial?.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-semibold text-primary">{testimonial?.name}</div>
                        <div className="text-sm text-text-secondary">
                          {testimonial?.role} at {testimonial?.company}
                        </div>
                        <div className="text-xs text-accent font-medium">
                          Project: {testimonial?.project}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Testimonial Navigation */}
              <div className="flex justify-center space-x-2 mt-6">
                {testimonials?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentTestimonial 
                        ? 'bg-accent w-6' :'bg-border hover:bg-text-secondary'
                    }`}
                    aria-label={`View testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Achievement Stats */}
        <div className="mt-16 animate-fade-in-up" style={{ animationDelay: '400ms' }}>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {achievements?.map((achievement, index) => (
              <div
                key={achievement?.label}
                className="bg-card rounded-xl p-6 text-center shadow-soft hover:shadow-medium transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-accent group-hover:scale-110 transition-all duration-300">
                  <Icon 
                    name={achievement?.icon} 
                    size={24} 
                    className="text-accent group-hover:text-accent-foreground transition-colors duration-300" 
                  />
                </div>
                
                <div className="text-2xl font-bold text-primary mb-2">
                  {achievement?.value}
                </div>
                
                <div className="font-semibold text-primary mb-1">
                  {achievement?.label}
                </div>
                
                <div className="text-sm text-text-secondary">
                  {achievement?.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center animate-fade-in-up" style={{ animationDelay: '600ms' }}>
          <div className="bg-gradient-to-r from-accent to-primary rounded-xl p-8">
            <h3 className="text-2xl font-bold text-accent-foreground mb-4">
              Ready to Work Together?
            </h3>
            <p className="text-accent-foreground/80 mb-6 max-w-2xl mx-auto">
              Join the list of satisfied clients and let's build something amazing together. From concept to deployment, I'm here to help bring your ideas to life.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-6 py-3 bg-accent-foreground text-accent rounded-lg font-medium hover:bg-accent-foreground/90 transition-colors duration-300 flex items-center justify-center space-x-2">
                <Icon name="MessageCircle" size={18} />
                <span>Start a Project</span>
              </button>
              
              <button className="px-6 py-3 border border-accent-foreground/30 text-accent-foreground rounded-lg font-medium hover:bg-accent-foreground/10 transition-colors duration-300 flex items-center justify-center space-x-2">
                <Icon name="Calendar" size={18} />
                <span>Schedule a Call</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;